/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IExpertItem } from 'pages/profile/interface';
import {
  ArticleState,
  IArticle,
  IArticleFilterByExpertsPayload,
  IArticleFilterPayload,
  IArticleRequest,
  ICreateArticleForm,
  IPublishArticle,
  IUpdateArticle,
  ModalStates,
} from './interface';

export const initialState: ArticleState = {
  loading: false,
  isActive: false,
  entId: '',
  articleFormData: {
    title: '',
    author: '',
    url: '',
    highlight: '',
    bannerImgUrl: '',
    lead: '',
    body: '',
    assignedGroups: [],
    highlightMedia: '',
    products: [],
    experts: [],
    contents: [],
    groups: [],
    categories: [],
  },
  previewArticle: {
    _id: '',
    isActive: false,
    entId: '',
    title: '',
    author: {
      id: '',
      firstName: '',
      lastName: '',
      profileImgUrl: '',
      bio: '',
    },
    url: '',
    highlight: '',
    bannerImgUrl: '',
    lead: '',
    body: '',
    highlightMedia: {
      _id: '',
      title: '',
      contentUrl: '',
      fileType: '',
      fileCategory: '',
      thumbnail: '',
      fileSize: 0,
    },
    assignedGroups: [],
    assignedGroupList: [],
    products: [],
    experts: [],
    contents: [],
    groups: [],
    createdAt: '',
    categories: [],
  },
  articles: [],
  total: 0,
  experts: [],
};

export const articleSlice = createSlice({
  name: 'feature/ent-acc/edit-ent-acc/expert/create-article',
  initialState,
  reducers: {
    getExperts(state: ArticleState) {
      state.loading = true;
    },
    getExpertsSucceeded(state: ArticleState, action: PayloadAction<IExpertItem[]>) {
      state.experts = action.payload;
      state.loading = false;
    },
    getExpertsFailed(state: ArticleState) {
      state.loading = false;
    },
    searchArticle(state: ArticleState, action: PayloadAction<IArticleFilterPayload>) {
      state.loading = true;
    },
    searchArticleSucceeded(state: ArticleState, action: PayloadAction<IArticleRequest>) {
      state.articles = action.payload.articles.map((article) => ({
        ...article,
        modalState: ModalStates.close,
      }));
      state.total = action.payload.total;
      state.loading = false;
    },
    searchArticleFailed(state: ArticleState) {
      state.loading = false;
    },
    fetchArticles(state:ArticleState, action:PayloadAction<IArticleFilterByExpertsPayload>) {
      state.loading = true;
    },
    fetchArticlesSucceeded(state:ArticleState, action:PayloadAction<IArticleRequest>) {
      state.loading = false;
      state.articles = action.payload.articles;
      state.total = action.payload.total;
    },
    fetchArticlesFailed(state:ArticleState) {
      state.loading = false;
    },
    setEntId(state: ArticleState, action: PayloadAction<string>) {
      state.entId = action.payload;
    },
    deleteArticle(state: ArticleState, _action: PayloadAction<string>) {
      state.loading = true;
    },
    deleteArticleSucceeded(state: ArticleState, action: PayloadAction<string>) {
      const temp = state.articles.filter((article) => article._id !== action.payload);
      state.articles = [...temp];
      state.loading = false;
    },
    deleteArticleFailed(state: ArticleState) {
      state.loading = false;
    },
    previewArticle(state: ArticleState, action: PayloadAction<string>) {
      state.loading = true;
    },
    previewArticleSucceeded(state: ArticleState, action: PayloadAction<IArticle>) {
      state.articleFormData.title = action.payload.title ?? '';
      state.articleFormData.url = action.payload.url ?? '';
      state.articleFormData.highlight = action.payload.highlight ?? '';
      state.articleFormData.bannerImgUrl = action.payload.bannerImgUrl ?? '';
      state.articleFormData.lead = action.payload.lead ?? '';
      state.articleFormData.body = action.payload.body ?? '';
      state.articleFormData.author = action.payload.author ? action.payload.author.id : '';
      state.articleFormData.highlightMedia = action.payload.highlightMedia ? action.payload.highlightMedia._id : '';
      state.articleFormData.experts = action?.payload?.experts
        ? action.payload.experts.map((expert) => expert.id) : [];
      state.articleFormData.products = action?.payload?.products
        ? action.payload.products.map((product) => product._id) : [];
      state.articleFormData.contents = action?.payload?.contents
        ? action.payload.contents.map((content) => content._id) : [];
      state.articleFormData.assignedGroups = action?.payload?.assignedGroups
        ? action.payload.assignedGroups.map((g) => g._id) : [];
      state.articleFormData.categories = action.payload?.categories?.map((i) => i.id);

      // set preview article data
      state.previewArticle = action.payload;
      state.previewArticle.assignedGroupList = action?.payload?.assignedGroups ?? [];
      state.loading = false;
    },
    previewArticleFailed(state: ArticleState) {
      state.loading = false;
    },
    publishArticle(state: ArticleState, _action: PayloadAction<IPublishArticle>) {
      state.loading = true;
    },
    publishArticleSucceeded(state: ArticleState) {
      state.loading = false;
    },
    publishArticleFailed(state: ArticleState) {
      state.loading = false;
    },
    updateArticle(state: ArticleState, _action: PayloadAction<IUpdateArticle>) {
      state.loading = true;
    },
    updateArticleSucceeded(state: ArticleState) {
      state.loading = false;
    },
    updateArticleFailed(state: ArticleState) {
      state.loading = false;
    },
    // openModal(state: ArticleState, action: PayloadAction<IOpenModal>) {
    //   const temp = state.articles;
    //   temp.forEach((article) => {
    //     if (article._id === action.payload.id) {
    //       article.modalState = action.payload.state;
    //     }
    //   });
    //   state.articles = [...temp];
    // },
    // closeModal(state: ArticleState, action: PayloadAction<string>) {
    //   const temp = state.articles;
    //   temp.forEach((article) => {
    //     if (article._id === action.payload) {
    //       article.modalState = ModalStates.close;
    //     }
    //   });
    //   state.articles = [...temp];
    // },
    createArticle(state: ArticleState, action: PayloadAction<ICreateArticleForm>) {
      state.loading = true;
    },
    createArticleSucceeded(state: ArticleState) {
      state.loading = false;
    },
    createArticleFailed(state: ArticleState) {
      state.loading = false;
    },
    editActive(state: ArticleState, action: PayloadAction<boolean>) {
      state.isActive = action.payload;
    },
    setForPreview(state: ArticleState, action: PayloadAction<ICreateArticleForm>) {
      state.articleFormData = action.payload;
    },
    reset: () => initialState,
  },
});

export const { actions: articleActions } = articleSlice;
